import { useAuth } from '@Auth/useAuth';
import { MainMenu } from '@components/MainMenu/MainMenu';
import { Box } from '@mui/material';
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

// Constants
const STATSIG_CLIENT_KEY = 'client-QfZpisheMzACYDh9iZlaWthwaeOSP9LPurFUOsQUont';

// Styled components
const AppContainer = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      position: 'relative',
    }}
  >
    {children}
  </Box>
);

const LoadingContainer = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
    }}
  >
    Loading...
  </Box>
);

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { supabaseUser, isAuthenticated, baseUser } = useAuth();

  // Memoize Statsig plugins
  const statsigPlugins = useMemo(
    () => [new StatsigAutoCapturePlugin(), new StatsigSessionReplayPlugin()],
    []
  );

  // Initialize Statsig client only when baseUser is available
  const { client, isLoading: statsigLoading } = useClientAsyncInit(
    STATSIG_CLIENT_KEY,
    baseUser
      ? {
          userID: baseUser.id,
          email: baseUser.email,
          custom: {
            isAuthenticated: isAuthenticated,
            organization: baseUser.organization_id || '', // Convert null to empty string
          },
        }
      : { userID: 'anonymous' },
    {
      plugins: statsigPlugins,
    }
  );

  // Handle authentication and navigation
  useEffect(() => {
    if (!supabaseUser || !isAuthenticated) return;

    const path =
      location.pathname === '/' ? '/products/all-products' : location.pathname;

    navigate(path);
  }, [navigate, isAuthenticated, location.pathname, supabaseUser]);

  // Show loading state if either user or Statsig is not ready
  if (!baseUser || !client || statsigLoading) {
    return (
      <AppContainer>
        <LoadingContainer />
      </AppContainer>
    );
  }

  return (
    <StatsigProvider client={client}>
      <AppContainer>
        <MainMenu />
        <Outlet />
      </AppContainer>
    </StatsigProvider>
  );
};

export default App;
